import { InjectionToken, NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { NgxsModule } from '@ngxs/store';
import {
  featurePermissionGuardFn,
  rolePermissionGuardFn,
} from '@wilson/authorization';
import { canMatchFeatureGuardFn } from '@wilson/feature-flags';
import {
  FeatureName,
  RoleAction,
  RolePermissionSubject,
} from '@wilson/interfaces';
import { QualificationsV2State } from '@wilson/qualification-v2/common';
import { QualificationV2OverviewComponent } from './components/qualification-v2-overview/qualification-v2-overview.component';
import { SharedUserQualificationsV2OverviewComponent } from './components/shared-user-qualifications-v2-overview/shared-user-qualifications-v2-overview.component';
import { SharedUserQualificationsV2OverviewState } from './states/shared-user-qualifications-v2-overview/shared-user-qualifications-v2-overview.state';
import { UserQualificationsV2OverviewState } from './states/user-qualifications-v2-overview/user-qualifications-v2-overview.state';
import { QualificationTrainingsV2OverviewComponent } from './components/qualifications-trainings-v2-overview/qualification-trainings-v2-overview.component';
import { TrainingQualificationsV2OverviewState } from './states/qualification-trainings-v2-overview/qualification-trainings-v2-overview.state';
import { QualificationV2ConfigModule } from './types';

export const QUALIFICATION_V2_CONFIG =
  new InjectionToken<QualificationV2ConfigModule>('QUALIFICATION_V2_CONFIG');

const routes: Routes = [
  {
    path: '',
    component: QualificationV2OverviewComponent,
    data: {
      breadcrumb: 'page.qualification_management.title',
      featurePermission: FeatureName.Qualification,
      rolePermission: {
        action: RoleAction.Read,
        subject: RolePermissionSubject.UserQualificationV2,
      },
    },
    canActivate: [rolePermissionGuardFn, featurePermissionGuardFn],
  },
  {
    path: 'shared',
    component: SharedUserQualificationsV2OverviewComponent,
    data: {
      breadcrumb: 'page.shared-users-qualifications.overview',
      featurePermission: FeatureName.Qualification,
      rolePermission: {
        action: RoleAction.ReadShared,
        subject: RolePermissionSubject.UserQualificationV2,
      },
    },
    canActivate: [rolePermissionGuardFn, featurePermissionGuardFn],
  },
  {
    path: 'trainings',
    component: QualificationTrainingsV2OverviewComponent,
    data: {
      breadcrumb: 'page.shared-users-qualifications.trainings',
      featurePermission: FeatureName.Qualification,
      rolePermission: {
        action: RoleAction.Read,
        subject: RolePermissionSubject.QualificationTraining,
      },
    },
    canActivate: [rolePermissionGuardFn, featurePermissionGuardFn],
  },
  {
    path: 'profiles',
    data: {
      breadcrumb: 'general.qualification_profiles',
      breadcrumbInfo: {
        titleKey: 'general.qualification_profiles',
        contentKey: 'page.qualification_profiles.handbook_popover_content',
        linkUrl: 'page.qualification_profiles.handbook_url',
        linkTextKey: 'general.handbook',
      },
      featureFlag: 'qualification-profiles-v-1',
      featurePermission: FeatureName.Qualification,
      rolePermission: {
        action: RoleAction.Read,
        subject: RolePermissionSubject.QualificationProfile,
      },
    },
    canActivate: [rolePermissionGuardFn, featurePermissionGuardFn],
    canMatch: [canMatchFeatureGuardFn],
    loadChildren: () =>
      import('@wilson/qualification-v2/profiles/overview').then(
        (m) => m.QualificationProfilesOverviewModule,
      ),
  },
];

@NgModule({
  imports: [
    RouterModule.forChild(routes),
    NgxsModule.forFeature([
      UserQualificationsV2OverviewState,
      QualificationsV2State,
      SharedUserQualificationsV2OverviewState,
      TrainingQualificationsV2OverviewState,
    ]),
  ],
  exports: [RouterModule],
})
export class QualificationsV2CoreModule {}
